import Video from '../models/Video';
import Question from '../models/Question';
import { env } from '../config/environment';

// API configuration using environment variables
// For Free tier: Use direct Function App URL (configured in GitHub Actions)
// For local development: Use localhost URL for local Function App
const API_BASE_URL = (() => {
  // If we have a localhost URL, we're in local development
  if (env.apiBaseUrl && env.apiBaseUrl.includes('localhost')) {
    return env.apiBaseUrl;
  }
  
  // For production with Free tier, use the full Function App URL
  // (GitHub Actions sets VITE_API_BASE_URL to https://funcapp.azurewebsites.net/api)
  if (env.apiBaseUrl && env.apiBaseUrl.startsWith('https://')) {
    return env.apiBaseUrl;
  }
  
  // Fallback: if somehow no URL is configured, use relative (shouldn't happen)
  return env.apiBaseUrl || '';
})();

// Debug logging in development
if (env.isDevelopment || env.enableDebug) {
  console.log('🔧 API Configuration:', {
    apiBaseUrl: env.apiBaseUrl,
    API_BASE_URL,
    isProduction: env.isProduction,
    isDevelopment: env.isDevelopment
  });
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
  total?: number;
}

export class ApiService {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Video API methods
  static async getPopularVideos(limit: number = 10): Promise<Video[]> {
    const response = await this.request<Video[]>(`/videos/popular?limit=${limit}`);
    return response.data || [];
  }

  static async getAllVideos(page: number = 1, pageSize: number = 10): Promise<Video[]> {
    const response = await this.request<Video[]>(`/videos?page=${page}&pageSize=${pageSize}`);
    return response.data || [];
  }

  static async getVideosByCategory(category: string, limit: number = 10): Promise<Video[]> {
    const response = await this.request<Video[]>(`/videos/category/${category}?limit=${limit}`);
    return response.data || [];
  }

  static async getVideoById(id: number): Promise<Video | null> {
    const response = await this.request<Video>(`/videos/${id}`);
    return response.data;
  }

  // Question API methods
  static async getQuestions(page: number = 1, pageSize: number = 10, category?: string, isAnswered?: boolean): Promise<Question[]> {
    let url = `/questions?page=${page}&pageSize=${pageSize}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (isAnswered !== undefined) url += `&isAnswered=${isAnswered}`;
    
    const response = await this.request<Question[]>(url);
    return response.data || [];
  }

  static async getPopularQuestions(limit: number = 10): Promise<Question[]> {
    // Get questions ordered by views and likes
    const response = await this.request<Question[]>(`/questions?pageSize=${limit}&isAnswered=true`);
    return response.data || [];
  }

  static async getQuestionById(id: string): Promise<Question | null> {
    const response = await this.request<Question>(`/questions/${id}`);
    return response.data;
  }

  // Blog API methods
  static async getFeaturedBlogPosts(limit: number = 6): Promise<any[]> {
    try {
      const response = await this.request<any[]>(`/blog/posts?featured=true&pageSize=${limit}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch featured blog posts:', error);
      return [];
    }
  }

  static async getBlogPosts(page: number = 1, pageSize: number = 10): Promise<any[]> {
    try {
      const response = await this.request<any[]>(`/blog/posts?page=${page}&pageSize=${pageSize}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      return [];
    }
  }

  static async getBlogPostById(id: string): Promise<any | null> {
    try {
      const response = await this.request<any>(`/blog/posts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch blog post:', error);
      return null;
    }
  }

  static async createBlogPost(blogPost: {
    title: string;
    excerpt: string;
    content: string;
    author: string;
    category: string;
    tags: string[];
    imageUrl: string;
    imageAltText?: string;
    isPublished: boolean;
    isFeatured: boolean;
    readTimeMinutes: number;
    metaDescription?: string;
    externalLinks: string[];
  }): Promise<{ success: boolean; data?: any; message: string }> {
    try {
      // Build URL with function key if available
      const url = new URL(`${API_BASE_URL}/blog/posts`);
      if (env.apiFunctionKey) {
        url.searchParams.append('code', env.apiFunctionKey);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(blogPost),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          data: data.data,
          message: data.message || 'Blog post created successfully'
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to create blog post'
        };
      }
    } catch (error) {
      console.error('Failed to create blog post:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async getBlogCategories(): Promise<string[]> {
    try {
      const response = await this.request<string[]>('/blog/categories');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch blog categories:', error);
      return [];
    }
  }

  static async getBlogTags(): Promise<string[]> {
    try {
      const response = await this.request<string[]>('/blog/tags');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch blog tags:', error);
      return [];
    }
  }

  static async incrementBlogPostViews(id: string): Promise<boolean> {
    try {
      const response = await this.request<any>(`/blog/posts/${id}/view`, {
        method: 'POST'
      });
      return response.success || false;
    } catch (error) {
      console.error('Failed to increment blog post views:', error);
      return false;
    }
  }

  static async likeBlogPost(id: string): Promise<boolean> {
    try {
      const response = await this.request<any>(`/blog/posts/${id}/like`, {
        method: 'POST'
      });
      return response.success || false;
    } catch (error) {
      console.error('Failed to like blog post:', error);
      return false;
    }
  }

  static async searchBlogPosts(query: string): Promise<any[]> {
    try {
      const response = await this.request<any[]>(`/blog/search?q=${encodeURIComponent(query)}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to search blog posts:', error);
      return [];
    }
  }

  static async updateBlogPost(id: string, blogPost: {
    title: string;
    excerpt: string;
    content: string;
    author: string;
    category: string;
    tags: string[];
    imageUrl: string;
    imageAltText?: string;
    isPublished: boolean;
    isFeatured: boolean;
    readTimeMinutes: number;
    metaDescription?: string;
    externalLinks: string[];
  }): Promise<{ success: boolean; data?: any; message: string }> {
    try {
      // Build URL with function key if available
      const url = new URL(`${API_BASE_URL}/blog/posts/${id}`);
      if (env.apiFunctionKey) {
        url.searchParams.append('code', env.apiFunctionKey);
      }

      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(blogPost),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          data: data.data,
          message: data.message || 'Blog post updated successfully'
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to update blog post'
        };
      }
    } catch (error) {
      console.error('Failed to update blog post:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async deleteBlogPost(id: string): Promise<{ success: boolean; message: string }> {
    try {
      // Build URL with function key if available
      const url = new URL(`${API_BASE_URL}/blog/posts/${id}`);
      if (env.apiFunctionKey) {
        url.searchParams.append('code', env.apiFunctionKey);
      }

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'Blog post deleted successfully'
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to delete blog post'
        };
      }
    } catch (error) {
      console.error('Failed to delete blog post:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async getQuestionsByCategory(category: string, limit: number = 10): Promise<Question[]> {
    const response = await this.request<Question[]>(`/questions?category=${encodeURIComponent(category)}&pageSize=${limit}`);
    return response.data || [];
  }

  // Architecture Template API methods
  static async getArchTemplates(): Promise<any[]> {
    try {
      const response = await this.request<any[]>('/arch-templates');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch architecture templates:', error);
      return [];
    }
  }

  static async getArchTemplateById(id: string): Promise<any | null> {
    try {
      const response = await this.request<any>(`/arch-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch architecture template:', error);
      return null;
    }
  }

  static async requestArchTemplate(templateId: string, email: string): Promise<{ success: boolean; message: string }> {
    try {
      // Build URL with function key if available
      const url = new URL(`${API_BASE_URL}/arch-templates/${templateId}/request`);
      if (env.apiFunctionKey) {
        url.searchParams.append('code', env.apiFunctionKey);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'Template request sent successfully'
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to request template'
        };
      }
    } catch (error) {
      console.error('Failed to request architecture template:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async createArchTemplate(template: {
    title: string;
    description: string;
    highlights: string;
    isAvailable: boolean;
    icon: string;
    sortOrder: number;
  }, userEmail?: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // Build URL with function key if available
      const url = new URL(`${API_BASE_URL}/arch-templates`);
      if (env.apiFunctionKey) {
        url.searchParams.append('code', env.apiFunctionKey);
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add user email for admin authorization
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify(template)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'Template created successfully',
          data: data.data
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to create template'
        };
      }
    } catch (error) {
      console.error('Failed to create architecture template:', error);
      return {
        success: false,
        message: 'Failed to create template. Please try again later.'
      };
    }
  }

  // Course API methods
  static async getCourses(): Promise<any[]> {
    try {
      const response = await this.request<any[]>('/courses');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      return [];
    }
  }

  static async getCourseById(id: string): Promise<any | null> {
    try {
      const response = await this.request<any>(`/courses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch course:', error);
      return null;
    }
  }

  static async submitCourseEnquiry(enquiry: any): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<any>('/courses/enquiry', {
        method: 'POST',
        body: JSON.stringify(enquiry),
      });

      return {
        success: response.success,
        message: response.message || (response.success ? 'Enquiry submitted successfully' : 'Failed to submit enquiry')
      };
    } catch (error) {
      console.error('Failed to submit course enquiry:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  // Question Submission API methods
  static async submitQuestion(questionData: {
    userEmail: string;
    userName: string;
    questionText: string;
    category: string;
    priority?: string;
  }): Promise<{ success: boolean; message: string; submissionId?: string; errors?: string[] }> {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: questionData.userEmail,
          userName: questionData.userName,
          questionText: questionData.questionText,
          category: questionData.category,
          priority: questionData.priority || 'Normal'
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit question',
          errors: data.errors
        };
      }

      return {
        success: true,
        message: data.message || 'Question submitted successfully',
        submissionId: data.submissionId
      };
    } catch (error) {
      console.error('Failed to submit question:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  // Search API methods
  static async search(query: string, maxResults: number = 20, type?: 'questions' | 'videos'): Promise<SearchResponse> {
    try {
      let url = `/search?q=${encodeURIComponent(query)}&maxResults=${maxResults}`;
      if (type) url += `&type=${type}`;
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  }

  static async getSearchSuggestions(query: string): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/search/suggestions?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.suggestions || [];
    } catch (error) {
      console.error('Failed to get search suggestions:', error);
      return [];
    }
  }
}

// Search interfaces
export interface SearchResult {
  id: string;
  type: 'question' | 'video';
  title: string;
  description?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  tags?: string;
  createdAt: string;
  relevanceScore: number;
}

export interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  query: string;
  searchDuration: string;
}

// Convenience function for form submission
export const submitQuestion = async (formData: {
  userName: string;
  userEmail: string;
  question: string;
  category: string;
}): Promise<{ success: boolean; message: string; submissionId?: string; errors?: string[] }> => {
  return ApiService.submitQuestion({
    userEmail: formData.userEmail,
    userName: formData.userName,
    questionText: formData.question,
    category: formData.category,
    priority: 'Normal'
  });
};

// Newsletter subscription API
export const subscribeToNewsletter = async (email: string): Promise<{ success: boolean; message: string; errors?: string[] }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
        source: 'website'
      }),
    });

    const data = await response.json();

    // check if success is false and message is "Email is already subscribed to the newsletter"
    if (!data.Success && data.Message === 'Email is already subscribed to the newsletter') {
      return {
        success: false,
        message: 'Email is already subscribed to the newsletter',
        errors: []
      };
    }

    return {
      success: data.Success || false,
      message: data.Message || (data.Success ? 'Successfully subscribed!' : 'Subscription failed'),
      errors: data.Errors
    };
  } catch (error) {
    console.error('Failed to subscribe to newsletter:', error);
    return {
      success: false,
      message: 'Network error. Please try again later.'
    };
  }
};